class DensityPlot extends HTMLElement {
  constructor() {
    super();
    this.binnedData = JSON.parse(
      document.getElementById("binned-data").textContent,
    );
    if (!this.binnedData) {
      throw new Error("unable to load binned prices");
    }
  }
  connectedCallback() {
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          this.plotChart();
        }
      },
      { threshold: 0 },
    );

    // Data Values
    this.midpointValue = this.stringToFloat(
      this.getAttribute("midpoint-value"),
    );
    this.minValue = this.stringToFloat(this.getAttribute("min-value"));
    this.maxValue = this.stringToFloat(this.getAttribute("max-value"));
    // Plot attributes
    this.plotId = this.getAttribute("plot-id");
    this.margin = {
      top: Number(this.getAttribute("margin-top")),
      right: Number(this.getAttribute("margin-right")),
      bottom: Number(this.getAttribute("margin-bottom")),
      left: Number(this.getAttribute("margin-left")),
    };
    this.totalHeight = Number(this.getAttribute("total-height"));
    this.midpointDisplayText = this.getAttribute("midpoint-display-text");
    this.strokeColor = this.getAttribute("stroke-color") || "#176F6F";
    this.fillColor = this.getAttribute("fill-color") || "#E2F7F5";
    this.axisColor = this.getAttribute("axis-color") || "#14353C";
    this.plotStrokeWidth = Number(this.getAttribute("plot-stroke-width")) || 3;
    this.midpointColor = this.getAttribute("midpoint-color") || "#176F6F";
    this.axisOffset = Number(this.getAttribute("axis-offset") ?? -1);
    this.midpointLabelY = Number(this.getAttribute("midpoint-label-y")) || 20;
    this.bindEventListeners();
    this.observer.observe(this);
  }
  disconnectedCallback() {
    this.cleanupEventListeners();
  }
  bindEventListeners() {
    // Re-plot chart on window resize
    d3.select(window).on("resize", () => this.plotChart());
  }
  cleanupEventListeners() {
    d3.select(window).on("resize", null);
  }

  stringToFloat(str) {
    if (!str) {
      return null;
    }
    return parseFloat(str.replace(/,/g, ""));
  }
  plotChart() {
    // Remove any existing plot
    d3.select(`#${this.plotId}`).select("svg").remove();
    // The most complicated part of this chart is probably the X domain.
    // The X domain of the points is [0, number_of_bins - 1]
    // D3 will take care of scaling the points to the actual width of the plot, which is determined by the width of the plotId component, set in the HTML

    // If width looks weird, it might be because
    // during htmx-swap, when this plot is first calculated,
    // BOTH the original and new plot exist.
    // See this by placing a debugger in this function.
    // To get around this, you can hide the original plot with CSS.
    const totalWidth = document.getElementById(this.plotId).clientWidth;
    const plotWidth = totalWidth - this.margin.left - this.margin.right;
    const plotHeight = this.totalHeight - this.margin.top - this.margin.bottom;
    var svg = d3
      .select(`#${this.plotId}`)
      .append("svg")
      .attr("width", "100%") // This is what makes the chart responsive
      .attr("height", this.totalHeight)
      .append("g")
      .attr("transform", `translate(${this.margin.left},${this.margin.top})`);

    // get the data
    const data = this.binnedData;
    const points = data.map((d) => [d.bin, d.count]);

    // set the scalars
    const maxX = data.length - 1;
    const xScalar = d3.scaleLinear().domain([0, maxX]).range([0, plotWidth]);
    const maxY = data.reduce((max, d) => {
      return d.count > max ? d.count : max;
    }, 0);
    const yScalar = d3.scaleLinear().range([plotHeight, 0]).domain([0, maxY]);

    // Plot the Line
    svg
      .append("path")
      .datum(points)
      .attr("stroke", this.strokeColor)
      .attr("stroke-width", this.plotStrokeWidth)
      .attr("fill", "none")
      .attr(
        "d",
        d3
          .line()
          .curve(d3.curveBasis)
          .x(function (d) {
            return xScalar(d[0]);
          })
          .y(function (d) {
            return yScalar(d[1]);
          }),
      );

    // Plot the area
    svg
      .append("path")
      .datum(points)
      .attr("fill", this.fillColor)
      .attr("opacity", "1")
      .attr("stroke", "none")
      .attr(
        "d",
        d3
          .area()
          .curve(d3.curveBasis)
          .x(function (d) {
            return xScalar(d[0]);
          })
          .y1(plotHeight)
          .y0(function (d) {
            return yScalar(d[1]);
          }),
      );

    // add the x Axis
    svg
      .append("line")
      .attr("x1", this.axisOffset)
      .attr("y1", plotHeight)
      .attr("x2", plotWidth)
      .attr("y2", plotHeight)
      .attr("stroke", this.axisColor)
      .attr("stroke-width", 1);

    // add the y Axis
    svg
      .append("line")
      .attr("x1", this.axisOffset)
      .attr("y1", 0)
      .attr("x2", this.axisOffset)
      .attr("y2", plotHeight)
      .attr("stroke", this.axisColor)
      .attr("stroke-width", 0.8);

    // add the y Axis title
    // Y axis label:
    svg
      .append("text")
      .attr("class", "pv-text-body-xs")
      .attr("fill", "#6E7784")
      .attr("text-anchor", "end")
      .attr("transform", "rotate(-90)")
      .attr("y", -this.margin.left + 15)
      .attr("x", -this.margin.top)
      .text("Frequency");

    if (this.midpointValue) {
      this._plotMidpoint(svg, xScalar, plotHeight);
    }
  }
  _plotMidpoint(svg, xScalar, plotHeight) {
    // Plot vertical line for midpoint
    const scaledXForMidpoint = xScalar(this._rawXForMidpoint());
    svg
      .append("line")
      .attr("x1", scaledXForMidpoint)
      .attr("y1", 5)
      .attr("x2", scaledXForMidpoint)
      .attr("y2", plotHeight)
      .attr("stroke", this.midpointColor)
      .attr("stroke-width", 2)
      .attr("stroke-dasharray", "5,5");

    // Circle at top of line
    svg
      .append("circle")
      .attr("cx", scaledXForMidpoint)
      .attr("cy", 0)
      .attr("r", 3)
      .attr("stroke", this.midpointColor)
      .attr("stroke-width", 2)
      .attr("fill", "white");

    // Rectangle over circle
    const rectWidth = 50 + Math.floor(Math.log10(this.midpointValue) - 2) * 10;
    svg
      .append("rect")
      .attr("rx", 5)
      .attr("ry", 5)
      .attr("x", scaledXForMidpoint - rectWidth / 2)
      .attr("y", 3 - this.margin.top)
      .attr("width", rectWidth)
      .attr("height", 22)
      .attr("fill", "#02363D");
    svg
      .append("text")
      .attr("class", "pv-text-title-sm")
      .attr("fill", "white")
      .attr("y", this.midpointLabelY - this.margin.top)
      .attr("x", scaledXForMidpoint)
      .attr("text-anchor", "middle")
      .text(this.midpointDisplayText);
  }

  _rawXForMidpoint() {
    // Figure out REAL midpoint/ REAL range
    // then scale it to the binned domain, which is [0, numberOfBins - 1]
    const initialRange = this.maxValue - this.minValue;
    const midpointDist = this.midpointValue - this.minValue;
    const binnedDataRange = this.binnedData.length - 1;
    // scale to bin domain
    return (midpointDist / initialRange) * binnedDataRange;
  }
}
window.customElements.define("density-plot", DensityPlot);
